import { IconButton, Tooltip } from "@mui/material";
import { lunarPercent, lunarPhaseName, lunarDistance } from "../../utils/lunarphase";
import React from "react";


function Moon({ currentTime, size=24 }: { currentTime: Date, size?: number }) {
    // lunarAgePercent 0..1
    const p = lunarPercent(currentTime) as number;
    const phase = lunarPhaseName(currentTime);
    const distance = lunarDistance(currentTime);

    // illuminated fraction: 0..1 (0 = new, 1 = full)
    const illuminated = 1 - 2 * Math.abs(p - 0.5);

    // waxing if moving from new->full (0..0.5)
    const isWaxing = p > 0 && p <= 0.5;

    const sizeScale = 12;
    const r = (size + sizeScale * ((distance - 56) / (63.8 - 56) - 0.5)) / 2;

    // visual layout
    const cx = size / 2;
    const cy = size / 2;
    // shift controls how deep the crescent is; larger shift -> thinner crescent
    const shift = illuminated * r * 1.8;
    const shiftX = isWaxing ? -shift : shift;

    // human readable percent
    const percent = Math.round(illuminated * 100);

    return (
        <Tooltip title={`${phase} - ${percent}% illuminated`} arrow>
            <IconButton>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label={`Moon phase ${percent}% illuminated`} style={{ overflow: 'visible' }}>
                    <defs>
                        {/* Mask: white = visible illuminated area (intersection of center circle and shifted circle) */}
                        <mask id="moon-mask">
                            {/* start with black (hidden) */}
                            <rect x="0" y="0" width={size} height={size} fill="black" />
                            {/* white circle at center (the physical moon) */}
                            <circle cx={cx} cy={cy} r={r} fill="white" />
                            {/* subtract a shifted circle to carve the crescent shape:
                                by drawing a black circle overlapping the white, the intersection/shape will form the right/left-lit portion.
                                We achieve subtracting by placing a black circle on top of the white base within the mask.
                            */}
                            <circle cx={cx + shiftX} cy={cy} r={r} fill="black" />
                        </mask>

                        {/* Soft shadow for darker terminator */}
                        <radialGradient id="moon-grad" cx="30%" cy="30%" r="90%" gradientTransform="translate(-0.05,-0.05)">
                            {/* soft warm specular */}
                            <stop offset="0%" stopColor="#fff8e6" stopOpacity="1" />
                            <stop offset="8%" stopColor="#fff2c6" stopOpacity="1" />

                            {/* warmer, cartoony transition */}
                            <stop offset="25%" stopColor="#ffdf9a" stopOpacity="1" />
                            <stop offset="45%" stopColor="#ffd6b3" stopOpacity="0.98" />
                            <stop offset="62%" stopColor="#f7d0c1" stopOpacity="0.95" />

                            {/* warm limb and shadow */}
                            <stop offset="80%" stopColor="#e6b98f" stopOpacity="0.95" />
                            <stop offset="100%" stopColor="#c48a5f" stopOpacity="0.95" />
                        </radialGradient>

                        {/* warm ambient rim to make the moon pop */}
                        <radialGradient id="moon-rim" cx="65%" cy="35%" r="100%" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="rgba(255,245,220,0.14)" />
                            <stop offset="55%" stopColor="rgba(255,235,200,0.06)" />
                            <stop offset="100%" stopColor="rgba(255,225,190,0)" />
                        </radialGradient>

                        {/* gentle blur for the terminator (used by CSS filter on the dark globe) */}
                        <filter id="soft-blur" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="2.2" />
                        </filter>
                    </defs>

                    {/* dark globe (night side) #222 */}
                    <circle cx={cx} cy={cy} r={r} fill="rgba(0, 45, 255, 0.08)" style={{ filter: 'drop-shadow(0px 0px 4px rgba(255, 255, 255, 0.392))' }} />

                    {/* illuminated part using mask */}
                    <g mask="url(#moon-mask)">
                        <circle cx={cx} cy={cy} r={r} fill="url(#moon-grad)" />
                    </g>

                    {/* subtle rim highlight */}
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                </svg>
            </IconButton>
        </Tooltip>
    );
}

export default React.memo(Moon);